/**
 * Survey Display Name — single source of truth for "what name do we show for
 * this survey row?"
 *
 * THE TWO COLUMNS
 *
 * A survey row carries two different name columns, and they are not
 * interchangeable:
 *
 *   - `projectname`      the deterministic machine identifier. For a child it is
 *                        always `${parentName}_${shortName}` (plus `_2`, `_3`, …
 *                        for same-market sibling segments), recomputed on every
 *                        write by the backend's computeChildProjectName(). It
 *                        can never carry a PM-typed suffix, by design.
 *
 *   - `childProjectName` the free-form, PM-editable per-child label. This is
 *                        where a suffix like `_TEST` lives. NULL on parents.
 *
 * Before this helper existed, the surveys tables read `childProjectName` while
 * the Details modals, the Survey Overview dropdown, the exclusion multi-select
 * and the PM Dashboard popup read `projectname` — so the same child appeared as
 * `DXD-6216-4_US_TEST` in one place and `DXD-6216-4_US` in another.
 *
 * THE RULE
 *
 * Show `childProjectName` when the row is a child AND that label is non-empty.
 * Otherwise fall back to `projectname`. Parents always show `projectname`.
 *
 * WHY THE CHECK IS ON `parentid` (this is the important part)
 *
 * "Is this a child?" is answered by the row's OWN `parentid`, never by which
 * JSX branch is rendering it. Utils/groupSurveys.ts pushes ORPHAN children —
 * a child whose parent is not on the current page — into the top-level array,
 * so they render through the *parent* branch of the table. A helper that took
 * an `isChild` flag from the caller would hand those rows the parent treatment
 * and show the truncated name. Keying on `parentid` fixes that case for free
 * and lets groupSurveys.ts stay purely structural.
 *
 * WHY IT RETURNS '' AND NOT A PLACEHOLDER
 *
 * Call sites disagree about their empty state — the tables use "Title", the PM
 * Dashboard uses '—', the dropdowns use 'N.A.'. Returning '' lets each keep its
 * own, e.g. `getSurveyDisplayName(row) || "Title"`.
 *
 * Note for callers using `??`: this returns '' rather than null/undefined for a
 * missing name, so `?? 'N.A.'` will let the empty string through. Use `||`.
 *
 * NOT ESCAPED. Two call sites feed the result to dangerouslySetInnerHTML. That
 * is pre-existing behaviour and this helper returns exactly the strings those
 * sites already rendered, so it adds no new exposure — but it removes none
 * either. Do not treat the return value as sanitised.
 */

export interface SurveyDisplayNameFields {
  parentid?: string | null;
  childProjectName?: string | null;
  projectname?: string | null;
  name?: string | null;
}

export function getSurveyDisplayName(
  survey?: SurveyDisplayNameFields | null,
): string {
  if (!survey) return '';

  // A child is any row carrying a parentid. An empty string is treated as
  // "not a child" defensively: the column is NULL on parents in the DB, but a
  // serialised or legacy row could arrive with ''.
  const parentId = survey.parentid == null ? '' : String(survey.parentid).trim();
  if (parentId) {
    const childLabel = (survey.childProjectName || '').trim();
    if (childLabel) return childLabel;
  }

  // `name` stays as the final fallback: it was the tail of the chain every
  // call site used before this helper, and dropping it would blank out rows
  // whose projectname is empty.
  return (survey.projectname || '').trim()
    || (survey.name || '').trim()
    || '';
}
